<template>
  <div
    class="card mb-4 radius-7 plan-locked-wrapper"
    :class="{ 'plan-locked-wrapper--ura': disabledByPlan }"
  >
    <PlanFeatureLocked
      v-if="disabledByPlan"
      overlay
      title="Visao de Estrutura / URA bloqueada pelo plano"
      message="Esta visualizacao mostra como categorias, subcategorias, servicos e profissionais formam o fluxo de atendimento automatico. O plano atual nao inclui esse recurso."
    />
    <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25 radius-7">
      <h5 class="card-title mb-0">
        <i class="ri-git-branch-line me-2"></i>Visão de Estrutura / URA
      </h5>
      <button class="btn btn-outline-primary btn-sm" @click="fetchStructure" :disabled="loading || disabledByPlan">
        <i class="ri-refresh-line me-1"></i>Atualizar
      </button>
    </div>

    <div class="card-body">
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Carregando...</span></div>
      </div>

      <div v-else-if="error" class="alert alert-danger" role="alert">
        <i class="ri-alert-line me-2"></i>{{ error }}
      </div>

      <div v-else-if="!treeData.length" class="alert alert-info" role="alert">
        <i class="ri-information-line me-2"></i>Nenhuma estrutura encontrada. Verifique o endpoint ou cadastre os dados.
      </div>

      <div v-else class="ura-tree-svg-wrapper">
        <svg :width="svgWidth" :height="svgHeight" class="ura-tree-svg">
          <defs>
            <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="1" dy="3" stdDeviation="3" flood-opacity="0.3" />
            </filter>
          </defs>
          <!-- Links/Linhas-->
          <g class="links">
            <path
              v-for="(link, index) in links"
              :key="`link-${index}`"
              :d="link.path"
              fill="none"
              stroke="#6b7280"
              stroke-width="1.2"
              stroke-linecap="round"
              opacity="0.52"
            />
          </g>

          <!-- Nós com pontos pequenos e texto ao lado -->
          <g class="nodes">
            <g
              v-for="node in nodes"
              :key="node.id"
              class="node-group"
            >
              <!-- Ponto pequeno como indicador -->
              <circle :cx="node.x" :cy="node.y" :r="nodeRadius" :fill="nodeColor(node.type)" opacity="0.9" filter="url(#shadow)" class="node-dot" />
              
              <!-- Texto legível ao lado do ponto (completo) -->
              <text 
                :x="node.x + nodeRadius + 6" 
                :y="node.y + 5" 
                font-size="12" 
                font-weight="600" 
                fill="#1f2937" 
                stroke="#ffffff"
                stroke-width="2"
                paint-order="stroke fill"
                class="node-text"
              >
                {{ node.title }}
              </text>
              
              <!-- Tooltip -->
              <title>{{ node.title }}</title>
            </g>
          </g>
        </svg>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch } from "vue";
import { axios } from "@/config/axios";
import PlanFeatureLocked from "../Common/PlanFeatureLocked.vue";

type UraTreeNode = {
  id: string | number;
  title: string;
  type: "category" | "subcategory" | "service" | "user";
  children?: UraTreeNode[];
};

type LayoutNode = {
  id: string | number;
  title: string;
  type: "category" | "subcategory" | "service" | "user";
  x: number;
  y: number;
};

type LinkItem = { 
  source: { x: number; y: number }
  target: { x: number; y: number }
  path: string
};

export default defineComponent({
  name: "UraTreeView",
  components: {
    PlanFeatureLocked,
  },
  props: {
    apiEndpoint: { type: String, default: "/api/ura/structure" },
    disabledByPlan: { type: Boolean, default: false },
  },
  setup(props) {
    const treeData = ref<UraTreeNode[]>([]);
    const loading = ref(false);
    const error = ref<string | null>(null);

    const nodes = ref<LayoutNode[]>([]);
    const links = ref<LinkItem[]>([]);

    const svgWidth = ref(1800);
    const svgHeight = ref(600);
    const nodeRadius = 3;
    const rowStep = 32;

    const nodeColor = (type: UraTreeNode["type"]) => {
      switch (type) {
        case "category":
          return "#2563eb";
        case "subcategory":
          return "#d97706";
        case "service":
          return "#059669";
        case "user":
          return "#9333ea";
        default:
          return "#6b7280";
      }
    };

    // Quebra o texto em múltiplas linhas com limite de caracteres por linha
    const wrapTextLines = (text: string, charsPerLine = 28): string[] => {
      const lines: string[] = [];
      let remaining = text;
      while (remaining.length > 0) {
        lines.push(remaining.substring(0, charsPerLine));
        remaining = remaining.substring(charsPerLine);
      }
      return lines;
    };

    // Gera caminho Bezier curvo para as linhas (conecta os centros dos pontos)
    const generateBezierPath = (source: { x: number; y: number }, target: { x: number; y: number }): string => {
      const dx = target.x - source.x;
      const dy = target.y - source.y;
      const cpx = source.x + dx * 0.5;
      return `M${source.x},${source.y} C${cpx},${source.y} ${cpx},${target.y} ${target.x},${target.y}`;
    };

    const transformData = (raw: any[]): UraTreeNode[] => {
      if (!Array.isArray(raw)) return [];

      const serviceNode = (service: any): UraTreeNode => ({
        id: `service-${service.id}`,
        title: service.name || "(Serviço sem nome)",
        type: "service",
        children: (Array.isArray(service.users_services) ? service.users_services : []).map((usersService: any) => {
          const user = usersService.user || {};
          return {
            id: `user-${service.id}-${user.id || usersService.id}`,
            title: user.name ? `${user.name} (${user.phone || "sem telefone"})` : "(Usuário sem nome)",
            type: "user",
          };
        }),
      });

      return raw.map((category: any) => ({
        id: `category-${category.id}`,
        title: category.name || "(Categoria sem nome)",
        type: "category",
        children: [
          ...(Array.isArray(category.direct_services) ? category.direct_services : []).map(serviceNode),
          ...(Array.isArray(category.subcategories) ? category.subcategories : []).map((subcategory: any) => ({
            id: `subcategory-${subcategory.id}`,
            title: subcategory.name || "(Subcategoria sem nome)",
            type: "subcategory" as const,
            children: (Array.isArray(subcategory.services) ? subcategory.services : []).map(serviceNode),
          })),
        ],
      }));
    };

    // Estima a largura do texto completo para saber onde ele termina
    const estimateTextWidth = (title: string): number => {
      const charWidthPx = 7.5;
      return title.length * charWidthPx;
    };

    const buildTreeLayout = () => {
      nodes.value = [];
      links.value = [];

      let nextY = 40;
      const maxWidthByDepth: number[] = [];
      const measureDepth = (node: UraTreeNode, depth: number) => {
        maxWidthByDepth[depth] = Math.max(maxWidthByDepth[depth] || 0, estimateTextWidth(node.title));
        (node.children || []).forEach((child) => measureDepth(child, depth + 1));
      };
      treeData.value.forEach((root) => measureDepth(root, 0));

      const xByDepth: number[] = [50];
      for (let depth = 1; depth < maxWidthByDepth.length; depth += 1) {
        xByDepth[depth] = xByDepth[depth - 1] + maxWidthByDepth[depth - 1] + 90;
      }

      const layoutSubtree = (node: UraTreeNode, depth: number): LayoutNode => {
        const x = xByDepth[depth] || 50;

        const childLayouts: LayoutNode[] = [];
        if (node.children && node.children.length > 0) {
          for (const child of node.children) {
            const childLayout = layoutSubtree(child, depth + 1);
            childLayouts.push(childLayout);

            links.value.push({
              source: { x, y: 0 },
              target: { x: childLayout.x, y: childLayout.y },
              path: "",
            });
          }
        }

        const y = childLayouts.length
          ? (childLayouts[0].y + childLayouts[childLayouts.length - 1].y) / 2
          : nextY;

        if (childLayouts.length === 0) {
          nextY += rowStep;
        }

        const layoutNode: LayoutNode = {
          id: node.id,
          title: node.title,
          type: node.type,
          x,
          y,
        };

        nodes.value.push(layoutNode);

        // Calcula o ponto onde o texto termina (origem da linha após texto)
        const textWidth = estimateTextWidth(layoutNode.title);
        const textEndX = layoutNode.x + nodeRadius + 6 + textWidth;

        // Ajusta conexões para começar após o texto
        for (const childLayout of childLayouts) {
          const link = links.value.find(
            (l) => l.target.x === childLayout.x && l.target.y === childLayout.y
          );
          if (link) {
            link.source = { x: textEndX, y: layoutNode.y };
            link.path = generateBezierPath({ x: textEndX, y: layoutNode.y }, { x: childLayout.x, y: childLayout.y });
          }
        }

        return layoutNode;
      };

      for (const root of treeData.value) {
        layoutSubtree(root, 0);
      }

      const maxY = Math.max(...nodes.value.map((n) => n.y), 500);
      const maxX = Math.max(...nodes.value.map((n) => n.x + estimateTextWidth(n.title)), 1100);
      svgWidth.value = Math.max(1200, maxX + 60);
      svgHeight.value = Math.max(420, maxY + 48);
    };

    const fetchStructure = async () => {
      if (props.disabledByPlan) {
        return;
      }

      loading.value = true;
      error.value = null;

      try {
        const response = await axios.get(props.apiEndpoint);
        treeData.value = transformData(response.data);
        buildTreeLayout();
      } catch (e: any) {
        console.error("Erro ao buscar estrutura URA:", e);
        error.value = e?.response?.data?.message || "Não foi possível carregar a estrutura URA. Confira o endpoint ou o backend.";
        treeData.value = [];
        nodes.value = [];
        links.value = [];
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      if (!props.disabledByPlan) {
        fetchStructure();
      }
    });

    watch(() => props.disabledByPlan, (disabled) => {
      if (!disabled && !treeData.value.length) {
        fetchStructure();
      }
    });

    return {
      treeData,
      loading,
      error,
      nodes,
      links,
      svgWidth,
      svgHeight,
      nodeRadius,
      nodeColor,
      fetchStructure,
    };
  },
});
</script>


<style scoped>
.ura-tree-svg-wrapper {
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #fafbfc 0%, #f3f4f6 100%);
  min-height: 700px;
  max-height: 1000px;
}

.plan-locked-wrapper {
  overflow: hidden;
  position: relative;
}

.plan-locked-wrapper--ura {
  min-height: 340px;
}

.plan-locked-wrapper--ura .card-body {
  min-height: 170px;
}

@media (max-width: 767px) {
  .plan-locked-wrapper--ura {
    min-height: 380px;
  }
}

.ura-tree-svg {
  min-width: 100%;
  min-height: 100%;
  display: block;
}

.node-group {
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.node-group:hover .node-dot {
  r: 7;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
}

.node-group:hover .node-text {
  font-weight: 700;
  fill: #000;
}

.node-dot {
  transition: all 0.2s ease-in-out;
}

.node-text {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  letter-spacing: -0.2px;
  pointer-events: none;
  transition: all 0.2s ease-in-out;
}
</style>
